import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MapPin, Users, Bed, Calendar, ArrowRight, ArrowLeft, AlertCircle, Building } from 'lucide-react';
import Card from '../components/UI/Card';
import LoadingSpinner from '../components/UI/LoadingSpinner';

const Listings = () => {
  const { t } = useTranslation();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const loadListings = async () => {
      try {
        const response = await fetch('/properties.json');
        if (!response.ok) {
          throw new Error('Failed to load listings');
        }
        const config = await response.json();
        setListings(config.listings);
      } catch (err) {
        console.error('Failed to load listings:', err);
        setError('Failed to load listings. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadListings();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card glass className="p-8 text-center max-w-md">
          <div className="text-red-400 mb-4">
            <AlertCircle className="w-12 h-12 mx-auto mb-2" />
            <h2 className="text-xl font-bold">Error Loading Listings</h2>
          </div>
          <p className="text-gray-400 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn-primary"
          >
            Try Again
          </button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex justify-start mb-4">
            <button
              onClick={() => navigate('/')}
              className="btn-glass p-2 hover:bg-white/10 transition-colors duration-200"
              title={t('common.back', 'Back')}
            >
              <ArrowLeft className="w-6 h-6 text-blue-400" />
            </button>
          </div>
          <h1 className="text-4xl font-bold text-white mb-4">{t('listings.selectProperty')}</h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            {t('listings.chooseProperty')}
          </p>
        </div>

        {/* Listings Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {listings.map((listing) => (
            <Card
              key={listing.id}
              hover
              glow
              glass
              className="overflow-hidden group cursor-pointer"
              onClick={() => navigate(`/calendar?listing=${listing.id}`)}
            >
              <div className="block">
                {/* Property Image */}
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={`images/${listing.id}.jpeg`}
                    alt={listing.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800&h=600&fit=crop&crop=center';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  
                  {/* Property Type Badge */}
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-blue-500/80 text-white text-sm font-medium rounded-full backdrop-blur-sm">
                      {t(`propertyType.${listing.property_type}`)}
                    </span>
                  </div>

                  {/* View Calendar Button */}
                  <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="flex items-center space-x-2 bg-white/20 backdrop-blur-sm rounded-lg px-3 py-2 text-white">
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm font-medium">{t('listings.viewCalendar')}</span>
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>

                {/* Property Details */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-white mb-3 group-hover:text-blue-400 transition-colors">
                    {listing.name}
                  </h3>
                  
                  <div className="flex items-center space-x-2 text-gray-400 mb-4">
                    <MapPin className="w-4 h-4" />
                    <span>{listing.location}</span>
                  </div>

                  {/* Property Stats */}
                  <div className="flex items-center justify-between text-sm text-gray-400">
                    <div className="flex items-center space-x-1">
                      <Bed className="w-4 h-4" />
                      <span>{listing.bedrooms} {t('listings.bedrooms')}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Users className="w-4 h-4" />
                      <span>{t('listings.guests', { count: listing.max_guests })}</span>
                    </div>
                  </div>

                  {/* Action Button */}
                  <div className="mt-6">
                    <div className="btn-primary w-full text-center group-hover:bg-gradient-to-r group-hover:from-blue-600 group-hover:to-purple-600 transition-all duration-300">
                      <span className="flex items-center justify-center space-x-2">
                        <Calendar className="w-4 h-4" />
                        <span>{t('listings.viewCalendar')}</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </div>
                </div>
                </div>
              </Card>
          ))}
        </div>

        {/* Empty State */}
        {listings.length === 0 && (
          <div className="text-center py-16">
            <Building className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-400 mb-2">{t('listings.noProperties')}</h3>
            <p className="text-gray-500">{t('listings.noPropertiesDesc')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Listings;